import * as React from "react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import ActionConfirmationModal, { ACTION_CONFIGS } from "../common/ActionConfirmationModal";
import { drawerTableWrapperClassName } from "../common/Table/TableStyles";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import { ExportSourceLinksToCsvRequest, isExportSourceLinksToCsvRequest } from "../../models/Jobs/JobRequest";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { UserRole } from "../../models/User";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import SourceLinkExportDownloadButton from "./SourceLinkExportDownloadButton";
import { SourceLinksExportScope } from "./SourceLinksTable";

/**
 * The Source Links page's Manage drawer: export the view the table shows, and the organization's source link
 * exports with their downloads.
 */
/*
 * Unlike the other exports this one is not built from selected rows but from the table's filters, so the drawer
 * names what an export started now would contain instead of listing queued items.
 */
export const SourceLinkExports: React.FC<{
  organizationId: string;
  scope: SourceLinksExportScope | null;
  userRole: UserRole;
  isAssignedUser: boolean;
}> = ({ organizationId, scope, userRole, isAssignedUser }) => {
  // Constructed in the render body, never memoised or deferred: JobsRepository initialises its api field with
  // useApi(), so building one is a hook call, and inside useMemo or a queryFn React fails with
  // "Should have a queue".
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [jobToRemove, setJobToRemove] = useState<ExportSourceLinksToCsvRequest | null>(null);
  // The table always has one tab open, so without this an organization's whole map takes one export per type.
  // The other filters still apply; each row of the file names its entity type.
  const [allTypes, setAllTypes] = useState(false);
  const summary = allTypes ? scope?.allTypesSummary : scope?.summary;
  const filter = scope && (allTypes ? { ...scope.filter, entityType: null } : scope.filter);

  const exportsQuery = useQuery({
    queryKey: [QUERY_KEYS.SOURCE_LINK_EXPORTS, organizationId],
    queryFn: () => jobsRepository.getSourceLinksCsvExportStatus(organizationId),
    enabled: !!organizationId,
    // A download link works for an hour after it is fetched, so the list is re-fetched well inside that; while an
    // export is being built it is polled faster so the finished file appears without a reload.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (job) => job.status === JobRequestStatus.Pending || job.status === JobRequestStatus.Processing
      )
        ? 30_000
        : QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
  });

  const exports = (exportsQuery.data ?? [])
    .filter(isExportSourceLinksToCsvRequest)
    .sort((a, b) => Number(b.id) - Number(a.id)) as ExportSourceLinksToCsvRequest[];

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SOURCE_LINK_EXPORTS, organizationId] });
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          Exports {summary ?? "the links the table shows"}. The file is built in the background and appears below
          when it is ready.
        </p>
        <div className="flex items-center gap-2">
          <Checkbox
            id="source-links-export-all-types"
            checked={allTypes}
            onCheckedChange={(checked) => setAllTypes(checked === true)}
          />
          <Label htmlFor="source-links-export-all-types" className="text-sm font-normal">
            All entity types, not only the open tab
          </Label>
        </div>
        <ActionConfirmationModal
          actionConfig={ACTION_CONFIGS.EXPORT_SOURCE_LINKS}
          description={
            <p className="text-sm text-muted-foreground">
              Exports {summary}. The file is built in the background and appears under Exports when it is ready.
            </p>
          }
          onConfirm={async (reason) => {
            await jobsRepository.startSourceLinksCsvExport(organizationId, filter!, reason);
            await refresh();
            toast.success("Export queued", { description: "It will appear under Exports when it finishes." });
          }}
        >
          <Button variant="outline" size="sm" className="w-full" disabled={!scope}>
            <FileSpreadsheet className="size-4" />
            Export current view
          </Button>
        </ActionConfirmationModal>
      </div>

      <div className="flex items-center gap-3 py-1">
        <Separator className="flex-1" />
        <span className="text-xs font-medium text-muted-foreground"><b>Exports</b></span>
        <Separator className="flex-1" />
      </div>

      <div className={cn(drawerTableWrapperClassName, exportsQuery.isError && "opacity-50")}>
        <Table className="table-fixed" containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%] min-w-[48px] text-center" />
              <TableHead className="w-[45%]">Name</TableHead>
              <TableHead className="w-[25%]">Status</TableHead>
              <TableHead className="w-[15%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {exports.map((job) => (
              <TableRow key={job.id}>
                <TableCell className="text-center">
                  <SourceLinkExportDownloadButton job={job} />
                </TableCell>
                <TableCell>
                  <span className="text-sm truncate block" title={job.exportName}>{job.exportName}</span>
                </TableCell>
                <TableCell>
                  <JobRequestStatusChip status={job.status} />
                </TableCell>
                <TableCell>
                  {job.status !== JobRequestStatus.Processing && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive"
                      aria-label={job.status === JobRequestStatus.Completed ? "Remove" : "Abort"}
                      onClick={() => setJobToRemove(job)}
                      disabled={!isAssignedUser && userRole !== UserRole.Admin}
                    >
                      {job.status === JobRequestStatus.Completed ? (
                        <Trash2 className="size-4" />
                      ) : (
                        <XCircle className="size-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {exports.length === 0 && !exportsQuery.isFetching && (
              <TableRow>
                <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                  No exports.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {jobToRemove && (
        <AbortJobModal
          open={jobToRemove !== null}
          jobRequest={jobToRemove}
          onClose={() => setJobToRemove(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(jobToRemove.id);
            await refresh();
            setJobToRemove(null);
          }}
        />
      )}
    </div>
  );
};

export default SourceLinkExports;
